import { ErrorBoundary } from 'react-error-boundary';

import { useAccount } from '@frontend/api';
import {
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Spinner,
  UseDisclosureProps,
} from '@frontend/components';

import { GenericError } from 'modules/common/components';

import { AccountForm } from './AccountForm';
import PaymentTermsDrawer from './PaymentTermsDrawer';
import { MODES } from './data/constants';

type AccountDrawerProps = UseDisclosureProps & {
  mode: (typeof MODES)[keyof typeof MODES];
  accountId?: number;
  callback: (args: { account: any }) => void;
  prefilledAccountName?: string;
};

const AccountDrawer = ({
  mode,
  accountId,
  callback,
  prefilledAccountName,
  ...disclosureProps
}: AccountDrawerProps) => {
  const { isOpen } = disclosureProps;

  const { data: account, isFetching: isLoading } = useAccount(
    { accountId },
    { queryOptions: { enabled: !!accountId && isOpen } },
  );

  return (
    <Drawer {...disclosureProps}>
      <DrawerContent>
        <DrawerHeader>
          {mode == MODES.EDIT ? 'Edit Account' : 'Create Account'}
          <DrawerCloseButton />
        </DrawerHeader>
        <ErrorBoundary FallbackComponent={() => <GenericError pt={3} />}>
          {isLoading ? (
            <Center flex={1}>
              <Spinner size="lg" />
            </Center>
          ) : (
            <AccountForm
              mode={mode}
              account={accountId ? account : undefined}
              prefilledAccountName={prefilledAccountName}
              onSaveCallback={callback}
              onClose={disclosureProps.onClose}
            />
          )}
        </ErrorBoundary>
        <PaymentTermsDrawer />
      </DrawerContent>
    </Drawer>
  );
};

export default AccountDrawer;
